import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { BOOST_PACKS, POINTS_PACKS, SPONSOR_PACK_DISCOUNT, tierById } from '@/lib/sponsor/catalog';
import { fmtDate, num, usd } from '@/lib/sponsor/pricing';
import { AD_STATUS_LABEL, buyPoints, resubmitAd, useSponsorStore } from '@/lib/sponsor/store';
import type { AdStatus, SponsorAd, SponsorOrder, SponsorPayment } from '@/lib/sponsor/types';
import { useSponsorContext } from '../context';
import DataTable, { Column } from '@/components/common/DataTable';

const DAY = 86_400_000;

const pillTone: Record<string, string> = {
  live: 'bg-accent-100 text-accent-800',
  scheduled: 'bg-background-200 text-foreground-600',
  checking: 'bg-secondary-100 text-secondary-800',
  needs_changes: 'bg-secondary-100 text-secondary-800',
  ended: 'bg-background-200 text-foreground-500',
  paid: 'bg-accent-100 text-accent-800',
  waiting: 'bg-secondary-100 text-secondary-800',
};

/** Simulated delivery until the apps report real view counts through the Loyalty API. */
function delivery(order: SponsorOrder, now: number) {
  const tier = tierById(order.tier);
  const limit = tier.stops[order.stopIndex] * 1000;
  const live = order.status === 'live' || order.status === 'ended';
  const totalDays = order.months * 30;
  const dayN = live ? Math.max(1, Math.min(totalDays, Math.ceil((now - new Date(order.start).getTime()) / DAY))) : 0;
  const dayOfMonth = ((dayN - 1) % 30) + 1;
  const monthFrac = live ? dayOfMonth / 30 : 0;
  const views = Math.round(limit * monthFrac * 0.97);
  const days = [...Array(14)].map((_, k) => {
    const d = new Date(now - (13 - k) * DAY);
    const wk = [0, 6].includes(d.getDay()) ? 0.72 : 1;
    const v = (limit / 30) * wk * (0.82 + 0.18 * Math.sin(k * 1.7) + 0.06 * (k % 3));
    const active = !live || d.getTime() >= new Date(order.start).getTime() - DAY;
    const two = tier.apps.length > 1;
    return { d, survey: active ? (two ? v * 0.58 : v) : 0, idi: active && two ? v * 0.42 : 0 };
  });
  return {
    tier, limit, live, totalDays, dayN, views,
    taps: Math.round(views * 0.028),
    answers: Math.round(tier.surveyAnswers * monthFrac * 0.9),
    pointsGiven: Math.round(tier.includedPoints * monthFrac * 0.85),
    days,
  };
}

export default function SponsorDashboardPage() {
  const store = useSponsorStore();
  const navigate = useNavigate();
  const { setSelection } = useSponsorContext();
  const [tab, setTab] = useState<'packs' | 'boost'>('packs');
  const [toast, setToast] = useState('');
  const [now] = useState(() => Date.now());

  const accountId = store.currentAccountId;
  const order = [...store.orders].reverse().find((o) => o.accountId === accountId);
  if (!accountId || !order) return <Navigate to="/sponsor" replace />;

  const d = delivery(order, now);
  const t = d.tier;
  const budget = t.includedPoints + (store.extraPoints[accountId] ?? 0);
  const payments = store.payments.filter((p) => p.accountId === accountId).slice().reverse();
  const notices = store.notices.filter((n) => n.accountId === accountId).slice(0, 5);
  const max = Math.max(1, ...d.days.map((x) => x.survey + x.idi));
  const sp = t.sponsorPrice;

  const flash = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2600);
  };

  const buy = (item: string, amount: number, pts: number) => {
    buyPoints(accountId, item, amount, pts);
    flash(`${num(pts)} pt added to your users' budget`);
  };

  const adStatus = (s: AdStatus) => {
    if (order.status === 'waiting_payment') return { label: 'Waiting for payment', tone: pillTone.waiting };
    return { label: AD_STATUS_LABEL[s], tone: pillTone[s] };
  };

  const kpis = [
    { k: 'Views this month', v: num(d.views), s: `of ${num(d.limit)} · ${num(d.limit - d.views)} left`, p: d.views / d.limit },
    { k: 'Taps on your ads', v: num(d.taps), s: `${(d.views ? (d.taps / d.views) * 100 : 0).toFixed(1)}% of views` },
    t.surveyAnswers
      ? { k: 'Survey answers', v: num(d.answers), s: `of ${num(t.surveyAnswers)} answers`, p: d.answers / t.surveyAnswers }
      : { k: 'Survey answers', v: `Not in ${t.name}`, s: 'Upgrade to Growth for a survey', lock: true },
    budget
      ? { k: 'Points left for users', v: `${num(budget - d.pointsGiven)} pt`, s: `${num(d.pointsGiven)} pt given of ${num(budget)}`, p: (budget - d.pointsGiven) / budget }
      : { k: 'Points for users', v: `Not in ${t.name}`, s: 'Buy a points pack below', lock: true },
  ];

  const adColumns: Column<SponsorAd>[] = [
      {
        key: 'title',
        header: 'Ad',
        sortable: true,
        render: (a) => <span className="font-semibold text-foreground-950">{a.title}</span>,
      },
      {
        key: 'type',
        header: 'Type',
        sortable: true,
        render: (a) => <span className="text-xs text-foreground-500 font-medium capitalize">{a.type}</span>,
      },
      {
        key: 'showsIn',
        header: 'Shows In',
        render: (a) => <span className="text-xs text-foreground-700">{a.showsIn}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (a) => {
          const st = adStatus(a.status);
          return (
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${st.tone}`}>
              {st.label}
            </span>
          );
        },
      },
      {
        key: 'views',
        header: 'Views',
        align: 'right',
        render: (a) => (
          <span className="font-semibold tabular-nums text-xs">
            {a.status === 'live' && a.share ? num(d.views * a.share) : '–'}
          </span>
        ),
      },
    ];

  const paymentColumns: Column<SponsorPayment>[] = [
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        render: (p) => <span className="text-xs text-foreground-600 whitespace-nowrap">{fmtDate(p.date)}</span>,
      },
      {
        key: 'item',
        header: 'Item',
        sortable: true,
        render: (p) => <span className="font-semibold text-foreground-950 text-xs sm:text-sm">{p.item}</span>,
      },
      {
        key: 'no',
        header: 'Number',
        render: (p) => <span className="text-xs font-mono text-foreground-500">{p.no}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (p) => (
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${pillTone[p.status]}`}>
            {p.status === 'paid' ? 'Paid' : 'Waiting for transfer'}
          </span>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        sortable: true,
        sortValue: (p) => p.amount,
        render: (p) => <span className="font-bold text-foreground-950 tabular-nums">{usd(p.amount)}</span>,
      },
    ];

  return (
    <div className="flex flex-col gap-5 max-w-6xl mx-auto">
      {order.status === 'waiting_payment' && (
        <div className="rounded-xl bg-secondary-100 text-secondary-900 px-4 py-3 text-sm">
          Invoice {order.no} for {usd(order.total)} is waiting for your bank transfer. OPS checks your ads after the money arrives.
        </div>
      )}
      {order.status === 'in_review' && (
        <div className="rounded-xl bg-primary-100 text-primary-900 px-4 py-3 text-sm">
          OPS is checking your licence and ads. Usually within 1 working day. We send an SMS when they go live.
        </div>
      )}
      {order.status === 'needs_changes' && (
        <div className="rounded-xl bg-secondary-100 text-secondary-900 px-4 py-3 text-sm flex flex-wrap gap-3 items-center justify-between">
          <span>OPS asked for a change: {order.changeNote}</span>
          <label className="px-3 py-1.5 rounded-lg border border-secondary-700 font-semibold cursor-pointer">
            Upload new banner
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  resubmitAd(order.id, f.name);
                  flash('New banner sent. OPS is checking it.');
                }
              }}
            />
          </label>
        </div>
      )}

      <section className="rounded-2xl p-5 bg-gradient-to-r from-foreground-950 to-foreground-800 text-background-50 flex flex-wrap gap-4 items-end justify-between shadow-xs">
        <div>
          <p className="text-xs text-background-300">Sponsor dashboard · simulated numbers</p>
          <h1 className="font-heading font-extrabold text-2xl flex items-center gap-2 flex-wrap">
            {order.company}
            <span className="text-[11px] font-bold bg-background-50 text-foreground-950 px-2 py-1 rounded shadow-2xs">
              {t.name.toUpperCase()}
            </span>
          </h1>
          <p className="text-sm text-background-300">
            {fmtDate(order.start)} to {fmtDate(order.end)} ·{' '}
            {d.live
              ? `day ${d.dayN} of ${d.totalDays} · ${d.totalDays - d.dayN} days left`
              : order.status === 'waiting_payment'
              ? 'starts after payment'
              : 'starts when OPS approves'}
          </p>
          <div className="mt-2 h-1.5 w-72 max-w-full rounded bg-background-50/20 overflow-hidden">
            <i className="block h-full bg-background-50" style={{ width: `${(d.dayN / d.totalDays) * 100}%` }}></i>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => document.getElementById('sp-points')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-4 py-2.5 rounded-lg bg-background-50 text-foreground-950 font-bold text-sm cursor-pointer hover:bg-neutral-100 transition-colors"
          >
            Buy points for users
          </button>
          <button
            type="button"
            onClick={() => {
              setSelection((s) => ({ ...s, tier: t.id === 'starter' ? 'growth' : 'premium' }));
              navigate('/sponsor');
            }}
            className="px-4 py-2.5 rounded-lg border border-background-50/40 font-bold text-sm cursor-pointer hover:bg-white/10 transition-colors"
          >
            Change package
          </button>
        </div>
      </section>

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => (
          <div
            key={k.k}
            className={`rounded-xl border p-4 flex flex-col gap-1 ${
              k.lock ? 'border-dashed border-background-300 bg-background-100' : 'border-background-200 bg-background-50 shadow-2xs'
            }`}
          >
            <span className="text-xs text-foreground-500">{k.k}</span>
            <b className={`font-heading ${k.lock ? 'text-base text-foreground-500' : 'text-2xl'}`}>{k.v}</b>
            <span className="text-xs text-foreground-500">{k.s}</span>
            {k.p != null && (
              <div className="h-1.5 rounded bg-background-200 overflow-hidden mt-1">
                <i className="block h-full bg-foreground-950" style={{ width: `${Math.min(100, k.p * 100)}%` }}></i>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-[1.7fr_1fr] items-start">
        <section className="bg-background-50 border border-background-200 rounded-xl p-4 flex flex-col gap-3 shadow-2xs">
          <div className="flex flex-wrap justify-between gap-2 items-baseline">
            <h2 className="font-heading font-bold">
              Views per day <small className="font-normal text-foreground-500">last 14 days{d.live ? '' : ' · example of what you will see'}</small>
            </h2>
            <div className="flex gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="w-2.5 h-2.5 rounded-sm bg-primary-500"></i>Survey app
              </span>
              {t.apps.length > 1 && (
                <span className="flex items-center gap-1.5">
                  <i className="w-2.5 h-2.5 rounded-sm bg-accent-500"></i>IDI app
                </span>
              )}
            </div>
          </div>
          <div
            className={`flex items-end gap-1.5 h-48 ${d.live ? '' : 'opacity-50'}`}
            role="img"
            aria-label={`Views per day over the last 14 days, up to ${num(max)} a day`}
          >
            {d.days.map((x, i) => (
              <div
                key={i}
                className="flex-1 flex flex-col items-center gap-1 min-w-0 h-full justify-end"
                title={`${x.d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}: ${num(x.survey + x.idi)} views`}
              >
                <div className="w-full max-w-5 flex flex-col justify-end h-full">
                  <div className="bg-accent-500 rounded-t-sm" style={{ height: `${(x.idi / max) * 100}%` }}></div>
                  <div className={`bg-primary-500 ${x.idi ? '' : 'rounded-t-sm'}`} style={{ height: `${(x.survey / max) * 100}%` }}></div>
                </div>
                <span className="text-[10px] text-foreground-400 h-3">{i % 2 ? x.d.getDate() : ''}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-50 border border-background-200 rounded-xl p-4 flex flex-col gap-2 shadow-2xs">
          <h2 className="font-heading font-bold">Your {t.name} benefits</h2>
          <ul className="flex flex-col gap-1.5 text-sm">
            {t.groups
              .flatMap((g) => g.items)
              .filter((x) => x.included)
              .map((x) => (
                <li key={x.label} className="grid grid-cols-[18px_1fr] gap-1">
                  <i className="ri-check-line text-accent-600" aria-hidden="true"></i>
                  {x.label}
                </li>
              ))}
          </ul>
          {notices.length > 0 && (
            <>
              <h3 className="font-semibold text-sm mt-3">SMS and email sent to you</h3>
              <ul className="flex flex-col gap-1 text-xs text-foreground-500">
                {notices.map((n) => (
                  <li key={n.id}>
                    {fmtDate(n.at)} · {n.text}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      {/* Unified Ads DataTable */}
      <section className="flex flex-col gap-3">
        <DataTable
          title="Your Ads"
          subtitle={`${order.ads.length} ad placement${order.ads.length > 1 ? 's' : ''}`}
          data={order.ads}
          columns={adColumns}
          getRowId={(a) => a.id}
          pagination={false}
          exportable={true}
          exportFilename="my-ads.csv"
          minWidth="600px"
        />
      </section>

      {/* Points section */}
      <section id="sp-points" className="bg-background-50 border border-background-200 rounded-xl p-4 flex flex-col gap-4 shadow-2xs">
        <div className="flex flex-wrap justify-between gap-2 items-baseline">
          <h2 className="font-heading font-bold">
            Points for your users{' '}
            <small className="font-normal text-foreground-500">
              users earn them from your ads and survey, then spend them at partner shops
            </small>
          </h2>
          <div className="inline-flex gap-1 p-1 rounded-lg bg-background-200/70">
            {([['packs', 'Points packs · 1 year'], ['boost', 'Boost · 7 days']] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={tab === id}
                onClick={() => setTab(id)}
                className={`px-3 py-1.5 rounded-md text-sm cursor-pointer transition-colors ${
                  tab === id ? 'bg-background-50 font-semibold shadow-sm text-foreground-950' : 'text-foreground-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {tab === 'packs' ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {POINTS_PACKS.map((p) => {
              const tot = p.points * (1 + p.bonusPct / 100);
              const spPrice = Math.round(p.price * (1 - SPONSOR_PACK_DISCOUNT));
              return (
                <div
                  key={p.name}
                  className={`rounded-xl border p-3.5 flex flex-col gap-1 ${
                    p.bonusPct >= 10 ? 'border-primary-400 bg-primary-50/50' : 'border-background-200 bg-background-50'
                  }`}
                >
                  <div className="flex justify-between items-center text-sm font-semibold">
                    {p.name}
                    {p.bonusPct > 0 && (
                      <span className="text-[11px] bg-primary-500 text-background-50 px-1.5 py-0.5 rounded font-bold">
                        +{p.bonusPct}%
                      </span>
                    )}
                  </div>
                  <b className="font-heading text-2xl">{num(tot)} pt</b>
                  <span className="text-xs text-foreground-500">
                    {p.bonusPct ? `Buy ${num(p.points)} + ${num(tot - p.points)} free · ` : ''}valid 1 year
                  </span>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      type="button"
                      disabled={!sp}
                      onClick={() => buy(`Points pack ${num(tot)} pt (sponsor price)`, spPrice, tot)}
                      className="rounded-lg py-2 text-xs font-bold bg-foreground-950 text-background-50 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-foreground-800 transition-colors"
                    >
                      {usd(spPrice)}
                    </button>
                    <button
                      type="button"
                      onClick={() => buy(`Points pack ${num(tot)} pt`, p.price, tot)}
                      className="rounded-lg py-2 text-xs font-bold border border-background-300 cursor-pointer hover:bg-background-100 transition-colors"
                    >
                      {usd(p.price)}
                    </button>
                  </div>
                  <span className="text-[11px] text-foreground-500">Sponsor price · list price</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BOOST_PACKS.map((b) => (
              <div key={b.name} className="rounded-xl border border-background-200 p-3.5 flex flex-col gap-1 bg-background-50">
                <span className="text-sm font-semibold">{b.name}</span>
                <b className="font-heading text-2xl">{num(b.points)} pt</b>
                <span className="text-xs text-foreground-500">{b.limit} · 7 days</span>
                <div className="flex justify-between items-baseline mt-2">
                  <b>{usd(b.price)}</b>
                  <span className="text-xs text-foreground-500">1 pt = ${(b.price / b.points).toFixed(4)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => buy(`${b.name} boost pack`, b.price, b.points)}
                  className="mt-1 rounded-lg py-2 text-sm font-bold bg-foreground-950 text-background-50 cursor-pointer hover:bg-foreground-800 transition-colors"
                >
                  Add to campaign
                </button>
              </div>
            ))}
          </div>
        )}
        {!sp && <p className="text-xs text-foreground-500">Sponsor price (save 10%) comes with Growth and Premium.</p>}
      </section>

      {/* Unified Payments DataTable */}
      <section className="flex flex-col gap-3">
        <DataTable
          title="Payments"
          subtitle={`${payments.length} payment records`}
          data={payments}
          columns={paymentColumns}
          getRowId={(p) => p.id}
          pagination={false}
          exportable={true}
          exportFilename="payments-history.csv"
          minWidth="600px"
        />
      </section>

      {toast && (
        <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-5 z-50 bg-foreground-950 text-background-50 px-4 py-2.5 rounded-lg text-sm shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
